import { EditPermissions } from '../../../../../../../shared/models';
import { getEditTypes } from '../../../delegatorUtils';
import { newStepValue } from '../../../../../services/flowBuilder/utils/constants';
import { FormControl, FormGroup } from '@angular/forms';
import { required } from '../../../../../../../shared/utils';

export interface RedisData {
  name: string,
  description: string,
  redisAction: string,
  yes: string,
  no: string
}

export interface LogicData {
  key: string,
  expire: string,
  value: string,
}

export interface RedisForms {
  RedisForm: FormGroup,
  LogicForm: FormGroup
}

export enum ActionType {
  DECREMENT = 'decr',
  DECREMENT_BY = 'decrBy',
  DELETE_ = 'del',
  EXIST = 'exist',
  GET_ = 'get',
  INCREMENT = 'incr',
  INCREMENT_BY = 'incrBy',
  SET = 'set',
}

export class Redis {

  action: any = {};

  constructor(formData: RedisData, logicData: LogicData) {
    this.action = {
      'actionType': 'redis',
    };
    this.setNodeData(formData, logicData);
  }

  static toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): RedisForms {
    const { editMenuTextOnly, editFlows } = getEditTypes(editPermissions);
    const formData = this.getFromData(stepData);
    const logicData = this.getLogicData(stepData);

    return {
      RedisForm: this.buildForm(formData, editFlows),
      LogicForm: this.buildLogicForm(logicData, formData.redisAction, editFlows),
    };
  }

  private static getFromData(stepData: { [index: string]: any }): RedisData {
    const action = stepData?.action ? stepData?.action : {};
    const actionParams = action?.actionParams ? action?.actionParams : {};
    const result = action?.result ? action?.result : {};

    return {
      name: action.name ? action.name : '',
      description: action.description ? action.description : '',
      redisAction: actionParams.redisAction ? actionParams.redisAction : '',
      yes: result.yes ? result.yes : newStepValue,
      no: result.no ? result.no : newStepValue,
    };
  }

  private static buildForm(formData: RedisData, editFlows: boolean): FormGroup {
    return new FormGroup({
      name: new FormControl({
        value: formData.name,
        disabled: !editFlows,
      }, required('Name')),
      description: new FormControl({
        value: formData.description,
        disabled: !editFlows,
      }, required('Description')),
      redisAction: new FormControl({
        value: formData.redisAction,
        disabled: !editFlows,
      }, required('Redis Action')),
      yes: new FormControl(formData.yes),
      no: new FormControl(formData.no),
    });
  }

  private static getLogicData(stepData: { [index: string]: any }): LogicData {
    const action = stepData?.action ? stepData?.action : {};
    const actionParams = action?.actionParams ? action?.actionParams : {};

    return {
      key: actionParams.key ? actionParams.key : '',
      expire: actionParams.expire ? actionParams.expire : '',
      value: actionParams.value ? actionParams.value : '',
    };
  }

  public static buildLogicForm(formData: LogicData, actionType: String, editFlows: boolean): FormGroup {
    switch (actionType) {
      case ActionType.DECREMENT:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new FormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, required('Expire')),
        });
      case ActionType.DECREMENT_BY:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new FormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, required('Expire')),
          value: new FormControl({
            value: formData.value,
            disabled: !editFlows,
          }, required('Value')),
        });
      case ActionType.DELETE_:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
        });
      case ActionType.EXIST:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
        });
      case ActionType.GET_:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
        });
      case ActionType.INCREMENT:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new FormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, required('Expire')),
        });
      case ActionType.INCREMENT_BY:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new FormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, required('Expire')),
          value: new FormControl({
            value: formData.value,
            disabled: !editFlows,
          }, required('Value')),
        });
      case ActionType.SET:
        return new FormGroup({
          key: new FormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new FormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, required('Expire')),
          value: new FormControl({
            value: formData.value,
            disabled: !editFlows,
          }, required('Value')),
        });
    }
  }

  get nodeData(): {} {
    return {
      action: this.action,
      'type': 'action',
      metadata: {
        nodeVersion: '1.0.0',
      },
    };
  }

  setNodeData(formData: RedisData, logicData: LogicData) {
    const { name, description, redisAction, yes, no } = formData;

    let actionParams: any = {
      redisAction,
    };

    if (logicData) {
      const { key, expire, value } = logicData;

      actionParams = {
        ...actionParams,
        ...this.getRedisAction(redisAction, logicData),
      };
    }

    const result = {
      yes,
      no,
    };

    this.action = {
      ...this.action,
      name,
      description,
      actionParams,
      result,
    };
  }

  getRedisAction(redisAction: string, logicData: LogicData): {} {
    const { key, expire, value } = logicData;

    const redis1: {} = {
      key,
    };
    const redis2: {} = {
      key,
      expire,
    };
    const redis3: {} = {
      key,
      expire,
      value,
    };

    switch (redisAction) {
      case ActionType.DECREMENT:
        return redis2;
      case ActionType.DECREMENT_BY:
        return redis3;
      case ActionType.DELETE_:
        return redis1;
      case ActionType.EXIST:
        return redis1;
      case ActionType.GET_:
        return redis1;
      case ActionType.INCREMENT:
        return redis2;
      case ActionType.INCREMENT_BY:
        return redis3;
      case ActionType.SET:
        return redis3;
    }
  }
}
